import { createElement, useCallback } from "react";

import { useDispatch } from "react-redux";

import { useTranslate } from "@similarweb/i18n-provider";

import { showErrorToast, showSuccessToast } from "app/actions/toast_actions";
import { renderErrorToast } from "app/pages/sales-intelligence/common-components/ErrorToast/ErrorToast";
import { SuccessToast } from "app/pages/sales-intelligence/common-components/SuccessToast/SuccessToast";

import { useUpdateTeamMembersMutation } from "../queries/useUpdateTeamMembersMutation";
import type { TeamListItem, TeamUser } from "../types/teams.types";
import { getAssignSuccessToastKey } from "../utils/getAssignSuccessToastKey";
import { getUserDisplayName } from "../utils/getUserDisplayName";
import { mergeTeamMemberUserIds } from "../utils/mergeTeamMemberUserIds";

import { useTeamsTrackingService } from "./useTeamsTrackingService";

const TOAST_DURATION_MS = 3000;
const MAX_PARALLEL_TEAM_UPDATES = 5;

type AssignUsersToTeamsParams = {
    teamIds: number[];
    users: TeamUser[];
    teams: readonly TeamListItem[];
};

export const useAssignUsersToTeams = () => {
    const dispatch = useDispatch();
    const { translate } = useTranslate();
    const tracking = useTeamsTrackingService();
    const { mutateAsync: updateTeamMembers, isPending } = useUpdateTeamMembersMutation();

    const showError = useCallback(() => {
        dispatch(
            showErrorToast(
                renderErrorToast({
                    content: translate("si.common.toast.error"),
                    minWidth: "auto",
                }),
            ),
        );
    }, [dispatch, translate]);

    const assignUsersToTeams = useCallback(
        async ({ teamIds, users, teams }: AssignUsersToTeamsParams): Promise<boolean> => {
            if (teamIds.length === 0 || users.length === 0) {
                return false;
            }

            const userIdsToAdd = users.map((user) => user.userId);

            let hasFailedUpdates = false;

            for (let start = 0; start < teamIds.length; start += MAX_PARALLEL_TEAM_UPDATES) {
                const teamIdsBatch = teamIds.slice(start, start + MAX_PARALLEL_TEAM_UPDATES);
                const batchResults = await Promise.all(
                    teamIdsBatch.map(async (teamId) => {
                        const team = teams.find((entry) => entry.teamId === teamId);
                        if (!team) {
                            return true;
                        }

                        const existingUserIds = team.users.map((user) => user.userId);
                        const nextUserIds = mergeTeamMemberUserIds(existingUserIds, userIdsToAdd);
                        const addedCount = nextUserIds.length - existingUserIds.length;

                        try {
                            await updateTeamMembers({ teamId, userIds: nextUserIds });

                            if (addedCount > 0) {
                                tracking.trackMembersAdded(team.name ?? "", addedCount);
                            }

                            return true;
                        } catch {
                            return false;
                        }
                    }),
                );

                if (batchResults.some((isSuccess) => !isSuccess)) {
                    hasFailedUpdates = true;
                }
            }

            if (hasFailedUpdates) {
                showError();
                return false;
            }

            const toastKey = getAssignSuccessToastKey(users.length, teamIds.length);
            const firstUserName = getUserDisplayName(users[0]);
            const firstTeam = teams.find((entry) => entry.teamId === teamIds[0]);
            const firstTeamName = firstTeam?.name ?? "";

            const toastContent =
                toastKey === "si.management.teams.users.assign.success.one_team"
                    ? translate(toastKey, { name: firstUserName, teamName: firstTeamName })
                    : toastKey === "si.management.teams.users.assign.success.many_teams"
                      ? translate(toastKey, { name: firstUserName, count: teamIds.length })
                      : toastKey === "si.management.teams.users.assign.success.many_users"
                        ? translate(toastKey, { count: users.length, teamName: firstTeamName })
                        : translate(toastKey, { usersCount: users.length, teamsCount: teamIds.length });

            dispatch(
                showSuccessToast(createElement(SuccessToast, { content: toastContent }), Date.now(), TOAST_DURATION_MS),
            );

            return true;
        },
        [dispatch, showError, tracking, translate, updateTeamMembers],
    );

    return { assignUsersToTeams, isPending };
};
